import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import UnifiedNavigation from '../components/common/UnifiedNavigation';
import HeroSection from '../components/modern/HeroSection';
import StatsSection from '../components/modern/StatsSection';
import FeaturedAnalysesSection from '../components/modern/FeaturedAnalysesSection';
import ThinkingTrainingSection from '../components/modern/ThinkingTrainingSection';
import FeaturedModelsSection from '../components/modern/FeaturedModelsSection';
import FeaturesSection from '../components/modern/FeaturesSection';
import HowItWorksSection from '../components/modern/HowItWorksSection';
import SocialProofSection from '../components/modern/SocialProofSection';
import FinalCTASection from '../components/modern/FinalCTASection';
import Footer from '../components/modern/Footer';
import AuthModal from '../components/modern/AuthModal';
import ErrorBoundary from '../components/common/ErrorBoundary';
import SEOHead from '../components/common/SEOHead';
import LazySection from '../components/common/LazySection';
import { useAccessibility, useKeyboardNavigation } from '../hooks/useAccessibility';
import { usePerformance } from '../hooks/usePerformance';

const ModernHome: React.FC = () => {
  console.log('🏠 ModernHome组件开始渲染');
  const { user, isAdminUser, validateSession } = useAuth();
  const [sessionValidated, setSessionValidated] = useState(true); // 直接设为true，因为AuthContext已经处理了认证
  const navigate = useNavigate();
  const location = useLocation();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');
  const [authRedirectTo, setAuthRedirectTo] = useState<string | undefined>(undefined);
  
  console.log('🏠 ModernHome状态:', { 
    hasUser: !!user, 
    sessionValidated,
    userEmail: user?.email 
  });
  
  // 可访问性功能
  const { preferences } = useAccessibility();
  useKeyboardNavigation();
  
  // 性能监控
  const { metrics, score } = usePerformance();
  
  // 在开发环境中显示性能指标
  if (import.meta.env.DEV) {
    console.log('Performance metrics:', metrics);
    console.log('Performance score:', score);
  }

  // 处理从其他页面跳转过来时的滚动定位
  useEffect(() => {
    if (location.state?.scrollTo) {
      const timer = setTimeout(() => {
        const element = document.getElementById(location.state.scrollTo);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100); // 延迟一点时间确保页面已渲染
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  // 移除额外的session验证，完全依赖AuthContext
  useEffect(() => {
    console.log('🔄 ModernHome直接使用AuthContext的认证状态');
    setSessionValidated(true);
  }, []);

  // 在session验证完成前显示加载状态
  if (!sessionValidated) {
    console.log('⏳ ModernHome显示加载状态，等待session验证完成');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">正在验证登录状态...</p>
          <p className="text-xs text-gray-400 mt-2">如果长时间停留在此页面，请检查控制台日志</p>
        </div>
      </div>
    );
  }

  console.log('🎉 ModernHome session验证完成，开始渲染主页内容');

  const handleLoginClick = () => {
    setAuthMode('login');
    setAuthRedirectTo(undefined); // No redirect, stay on current page
    setAuthModalOpen(true);
  };

  const handleSignUpClick = () => {
    setAuthMode('signup');
    setAuthRedirectTo(undefined); // No redirect, stay on current page
    setAuthModalOpen(true);
  };



  const handleStartTraining = () => {
    // 直接跳转到案例列表页，不管是否登录
    navigate('/analyses');
  };



  return (
    <>
      <SEOHead
        title="LeapWard - 基于热点新闻的深度思考分析平台"
        description="通过热点新闻案例学习深度分析方法，AI专家级解读，提升批判性思维和洞察力。每日精选案例，让思维过程变得清晰可见。"
        keywords="LeapWard,深度分析,批判性思维,新闻分析,AI解读,思维模型,案例学习,洞察力提升"
      />
      
      {/* 跳过链接 */}
      <a href="#main-content" className="skip-link">
        跳转到主要内容
      </a>
      
      <div className="min-h-screen bg-white">
      <ErrorBoundary>
        <UnifiedNavigation
          currentPage="home"
          sticky={true}
          onLoginClick={handleLoginClick}
          onSignUpClick={handleSignUpClick}
        />
      </ErrorBoundary>
      
      <main id="main-content">
        <ErrorBoundary>
          <HeroSection
            isLoggedIn={!!user}
            onStartTraining={handleStartTraining}
          />
        </ErrorBoundary>
        
        {/* 将案例精选提前，作为核心引流内容 */}
        <ErrorBoundary>
          <FeaturedAnalysesSection />
        </ErrorBoundary>

        {/* 思维训练区域 */}
        <ErrorBoundary>
          <ThinkingTrainingSection />
        </ErrorBoundary>

        {/* 模型工厂精选区域 */}
        <ErrorBoundary>
          <FeaturedModelsSection />
        </ErrorBoundary>

        <LazySection>
          <ErrorBoundary>
            <FeaturesSection />
          </ErrorBoundary>
        </LazySection>

        {/* 暂时移除数据展示模块，等有真实数据后再启用 */}
        {/* <LazySection>
          <ErrorBoundary>
            <StatsSection />
          </ErrorBoundary>
        </LazySection> */}
      
      <LazySection>
        <ErrorBoundary>
          <HowItWorksSection />
        </ErrorBoundary>
      </LazySection>
      
      <LazySection>
        <ErrorBoundary>
          <SocialProofSection />
        </ErrorBoundary>
      </LazySection>
      
      <LazySection>
        <ErrorBoundary>
          <FinalCTASection
            isLoggedIn={!!user}
            onStartTraining={handleStartTraining}
          />
        </ErrorBoundary>
      </LazySection>
      
        <ErrorBoundary>
          <Footer />
        </ErrorBoundary>
      </main>
      
      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        defaultMode={authMode}
        redirectTo={authRedirectTo}
      />
      </div>
    </>
  );
};

export default ModernHome;